import type { SearchParamsObject } from '@algolia/client-search';
import get from 'lodash/get';
//
// Note: this component is currently used
// for cms content article names. It is generalized so other
// Algolia based sources can be used by adding new AutocompleteType options.
import type {
  ChangeEvent,
  FocusEvent,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DynamicReactAutosuggest } from '#/components/ReactAutosuggest/DynamicReactAutosuggest';
import { Tag } from '#/components/Tag/Tag';
import { Text } from '#/components/Text/Text';
import { useAppConfig } from '#/config/AppConfigContext';
import type { AppConfig } from '#/config/types/AppConfig';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { PostSearchAlgoliaPublicContentHit } from '#/modules/postSearch/algolia/types/PostSearchAlgoliaPublicContentHit';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { staffApiFetchInvoices } from '#/modules/staff/api/invoices/staffApiFetchInvoices';
import type { StaffOrgDashboardApiInvoice } from '#/modules/staff/orgDashboard/invoices/api/types/StaffOrgDashboardApiInvoice';
import type { StaffAlgoliaPostSearchContentHit } from '#/modules/staff/postSearch/algolia/types/StaffAlgoliaPostSearchContentHit';
import { fontWeights } from '#/theme/theme';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';

import { AutosuggestWrapper } from './Autocomplete.styled';

export type Suggestion = {
  name: string;
  value: string;
  hit: PostSearchAlgoliaPublicContentHit;
  hits: (
    | PostSearchAlgoliaPublicContentHit
    | StaffAlgoliaPostSearchContentHit
  )[];
  invoice?: StaffOrgDashboardApiInvoice;
};

export type AutocompleteType =
  | 'CONTENT'
  | 'NEW_ORG_NAME'
  | 'SALARY_SURVEY_TITLES'
  | 'STAFF_SALARY_SURVEY_TITLES'
  | 'STAFF_INVOICE_PAYMENTS'
  | 'STAFF_CONTENT_POSTS'
  | 'STAFF_OBJECTS';

type AutocompleteOptions = {
  indexName: string;
  queryOptions: SearchParamsObject;
  responseValue: null | string;
  responseName: string;
};

function autocompleteOptionsMap(
  appConfig: AppConfig,
): Partial<Record<AutocompleteType, AutocompleteOptions>> {
  return {
    CONTENT: {
      indexName: appConfig.algolia.contentIndexName,
      queryOptions: {
        page: 0,
        hitsPerPage: 50,
        attributesToRetrieve: ['objectID', 'title'],
      },
      responseValue: 'objectID',
      responseName: 'title',
    },
    NEW_ORG_NAME: {
      indexName: appConfig.algolia.siteIndexName,
      queryOptions: {
        page: 0,
        hitsPerPage: 20,
        filters: 'type:ORG',
        attributesToRetrieve: ['objectId', 'name'],
      },
      responseValue: null,
      responseName: 'name',
    },
    SALARY_SURVEY_TITLES: {
      indexName: appConfig.algolia.compdbIndexName,
      queryOptions: {
        attributesToRetrieve: ['name', 'jobTitle'],
        filters: 'includeInSurvey:true',
        hitsPerPage: 20,
        ignorePlurals: true,
        page: 0,
        queryLanguages: [I18nLocale.EN],
        removeStopWords: true,
        removeWordsIfNoResults: 'allOptional',
      },
      responseValue: null,
      responseName: 'name',
    },
    STAFF_SALARY_SURVEY_TITLES: {
      indexName: appConfig.algolia.compDbStaffIndexName,
      queryOptions: {
        page: 0,
        hitsPerPage: 50,
        attributesToRetrieve: ['name', 'subFamilyId'],
      },
      responseValue: null,
      responseName: 'name',
    },
    STAFF_CONTENT_POSTS: {
      indexName: appConfig.algolia.contentStaffIndexName,
      queryOptions: {
        page: 0,
        hitsPerPage: 20,
        attributesToRetrieve: ['objectID', 'subsiteId', 'pages'],
        filters: 'type:CMS_PAGE_MASTER AND pages.status:PUBLISHED',
      },
      responseValue: 'objectID',
      responseName: 'title',
    },
    STAFF_OBJECTS: {
      indexName: appConfig.algolia.staffIndexName,
      queryOptions: {
        page: 0,
        hitsPerPage: 20,
        attributesToRetrieve: ['objectID', 'name', 'type'],
      },
      responseValue: 'objectID',
      responseName: 'name',
    },
  };
}

const staffPostSuggestion = ({ name, hit }: Suggestion) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <Text qaId="autocomplete-suggestion">{name}</Text>
    <Text styleAs="bodySmall" color="lightContentGrey">
      {hit.objectID}
    </Text>
  </div>
);

const staffContentSuggestion = ({ hit }: Suggestion) => (
  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
    <div>
      <Text qaId="autocomplete-suggestion">
        {hit.title}
        <Tag variant="primary" size="micro">
          {hit.pageType}
        </Tag>
      </Text>
      <Text styleAs="bodySmall" color="lightContentGrey">
        {hit.url}
      </Text>
    </div>
    <div>
      <Text styleAs="bodySmall" color="lightContentGrey">
        {hit.locale}
      </Text>
    </div>
  </div>
);

const responseToOption = (
  response: {
    hits: (
      | PostSearchAlgoliaPublicContentHit
      | StaffAlgoliaPostSearchContentHit
    )[];
  },
  autocompleteType: AutocompleteType,
  appConfig: AppConfig,
) => {
  const { responseValue, responseName } =
    autocompleteOptionsMap(appConfig)[autocompleteType]!;

  let hits: (
    | PostSearchAlgoliaPublicContentHit
    | StaffAlgoliaPostSearchContentHit
  )[] = [];

  /* Staff Content Index returns data representing all content under master ID.
   * Each page represents a hit.
   */
  if (autocompleteType === 'STAFF_CONTENT_POSTS') {
    hits = response.hits
      .flatMap((hit) => (hit as StaffAlgoliaPostSearchContentHit).pages)
      .filter((a) => Boolean(a)); // Remove nulls, some hits may not have pages
  } else {
    ({ hits } = response);
  }

  return hits.map(
    (hit) =>
      ({
        /* Provide the original hit regardless-- the selection is used appropriately. */
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        value: responseValue ? get(hit, responseValue.split('.')) : hit,
        // TODO: Fix this the next time the file is edited.
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
        name: responseName ? get(hit, responseName.split('.')) : hit,
        hits: response.hits, // Provide original hits for staff content
        hit,
      }) as Suggestion,
  );
};

async function promiseOptions(
  input: string,
  autocompleteType: AutocompleteType,
  filters: string | undefined,
  overrideSearchConfig: Readonly<{ apiKey: string; appId: string }> | undefined,
  onApiKeyTimeout: (() => Promise<string>) | undefined,
  appConfig: AppConfig,
) {
  const optionsMap = autocompleteOptionsMap(appConfig);

  return algoliaSearch<
    PostSearchAlgoliaPublicContentHit | StaffAlgoliaPostSearchContentHit
  >({
    config:
      overrideSearchConfig ?? getSearchAlgoliaDefaultConfig({ appConfig }),
    query: input,
    indexName: optionsMap[autocompleteType]!.indexName,
    options: {
      ...optionsMap[autocompleteType]!.queryOptions,
      filters: [filters, optionsMap[autocompleteType]!.queryOptions.filters]
        .filter((a) => Boolean(a))
        .join(' AND '),
    },
    analytics: true,
    onApiKeyTimeout,
    isUsingAlgoliaMock: appConfig.algolia.mock,
  });
}

type Props = {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (
    event: FocusEvent<HTMLInputElement>,
    suggestions?: Suggestion[],
  ) => void;
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  onSuggestionSelected?: (...args: any[]) => any;
  onSuggestionsUpdated?: ({
    suggestions,
    value,
  }: {
    suggestions?: Suggestion[];
    value: string;
  }) => void;
  shouldRenderSuggestions?: (arg0: string) => boolean;
  autocompleteType: AutocompleteType;
  filterOptions?: string;
  hasError: boolean;
  searchConfig?: {
    apiKey: string;
    appId: string;
  };
  large?: boolean;
  value: string;
  placeholder?: string;
  suggestionComponentFn?: ({ name, value, hit }: Suggestion) => ReactNode;
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line typescript/no-explicit-any
  updateApiKey?: (...args: any[]) => any;
  height?: string;
  qaId: string;
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  'onKeyPress' | 'id' | 'name' | 'type'
>;

export function AutocompleteTextInput({
  onChange,
  onBlur,
  onSuggestionSelected,
  onSuggestionsUpdated,
  shouldRenderSuggestions,
  autocompleteType,
  filterOptions,
  hasError,
  height,
  searchConfig,
  large,
  placeholder,
  qaId,
  suggestionComponentFn,
  updateApiKey,
  ...otherProps
}: Props) {
  const { appConfig } = useAppConfig();
  const [suggestions, updateSuggestions] = useState<Suggestion[]>([]);

  type QueryResult = readonly {
    name: unknown;
    value: unknown;
  }[];

  const inProgressQueryRef = useRef<Promise<QueryResult> | null>(null);
  const queuedValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (onSuggestionsUpdated) {
      onSuggestionsUpdated({
        suggestions,
        value: otherProps.value,
      });
    }
  }, [onSuggestionsUpdated, otherProps.value, suggestions]);

  const loadSuggestions = (value: string) => {
    // Cancel the previous request
    if (inProgressQueryRef.current) {
      queuedValueRef.current = value;

      return inProgressQueryRef.current;
    }

    let query;
    if (autocompleteType === 'STAFF_INVOICE_PAYMENTS') {
      query = staffApiFetchInvoices({
        number: value,
        outstanding: true,
      }).then((invoices) =>
        invoices.map(
          (i: StaffOrgDashboardApiInvoice) =>
            ({
              value: i.id,
              name: i.number.toString(),
              invoice: i,
            }) as Suggestion,
        ),
      );
    } else {
      query = promiseOptions(
        value,
        autocompleteType,
        filterOptions,
        searchConfig,
        updateApiKey,
        appConfig,
      ).then((r) => responseToOption(r, autocompleteType, appConfig));
    }

    inProgressQueryRef.current = query;

    void query.then((options) => {
      updateSuggestions(options);

      return undefined;
    });

    void query.finally(() => {
      if (inProgressQueryRef.current === query) {
        inProgressQueryRef.current = null;
      }

      const queuedValue = queuedValueRef.current;
      queuedValueRef.current = null;

      if (queuedValue && queuedValue !== value) {
        void loadSuggestions(queuedValue);
      }
    });

    return query;
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    void loadSuggestions(value);
  };

  const onSuggestionsClearRequested = () => {
    updateSuggestions([]);
    queuedValueRef.current = null;
  };

  const onChangeInternal = (
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line typescript/no-explicit-any
    event: Record<string, any>,
    {
      newValue,
    }: {
      newValue: string;
    },
  ) => {
    onChange({
      target: {
        value: newValue,
      },
    } as ChangeEvent<HTMLInputElement>);
  };

  const onBlurInternal = (event: FocusEvent<HTMLElement>) => {
    if (onBlur) {
      onBlur(event as FocusEvent<HTMLInputElement>, suggestions);
    }
  };

  const renderSuggestion = useCallback(
    (suggestion: Suggestion) => {
      if (suggestionComponentFn) {
        return <>{suggestionComponentFn(suggestion)}</>;
      }

      if (autocompleteType === 'STAFF_CONTENT_POSTS') {
        return staffContentSuggestion(suggestion);
      }

      if (autocompleteType === 'STAFF_OBJECTS') {
        return staffPostSuggestion(suggestion);
      }

      return (
        <Text
          qaId="autocomplete-suggestion"
          fontWeight={large ? fontWeights.semiBold : undefined}
        >
          {suggestion.name}
        </Text>
      );
    },
    [large, autocompleteType, suggestionComponentFn],
  );

  const inputProps = {
    ...otherProps,
    placeholder,
    'data-qa-id': qaId,
    onChange: onChangeInternal,
    onBlur: onBlurInternal,
  };
  return (
    <AutosuggestWrapper
      $height={height}
      $large={large}
      $borderSize={large ? 4 : 2}
      $hasError={hasError}
    >
      <DynamicReactAutosuggest<Suggestion>
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={(suggestion) => suggestion.name}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={shouldRenderSuggestions}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
      />
    </AutosuggestWrapper>
  );
}
